import React, { useMemo } from 'react'; 
import { useTranslation } from 'react-i18next';
import { Lightbulb, Shield, Users, Layers } from 'lucide-react'; 
import clsx from 'clsx';

// 🛰️ Local JSON datasets (Intelligence Assets)
import vocabulary from '../../../data/sparks/vocabulary.json';
import science from '../../../data/sparks/science.json';
import history from '../../../data/sparks/history.json';
import logic from '../../../data/sparks/logic.json';
import cleaning from '../../../data/sparks/cleaning.json';
import animal from '../../../data/sparks/animal.json';
import cooking from '../../../data/sparks/cooking.json';
import space from '../../../data/sparks/space.json';
import jokes from '../../../data/sparks/jokes.json';

const CATEGORIES: Record<string, any[]> = {
  vocabulary, science, history, logic, cleaning, animal, cooking, space, jokes
};

/**
 * 🧠 getDailySparks
 * Deterministic engine for daily content synchronization.
 */
export const getDailySparks = (activeCategories: string[]) => {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - startOfYear.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  
  return (activeCategories || []).map((cat: string) => {
    const pool = CATEGORIES[cat] || [];
    if (pool.length === 0) {
      return { category: cat, content: "Expansion pack coming soon...", type: "placeholder" };
    }
    const index = dayOfYear % pool.length;
    return { category: cat, ...pool[index] };
  });
};

interface SparkEngineProps {
  insights: any[]; 
  // 🛡️ Hybrid Scope Props
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * 💡 SPARK ENGINE
 * Refactored for Hybrid Scoping: Renders knowledge assets across Personal and Shared partitions.
 * Adheres to Google Data Privacy by siloing user-specific learning preferences.
 */
export const SparkEngine: React.FC<SparkEngineProps> = ({ 
  insights, 
  currentHouseholdId, 
  activeScope 
}) => {
  const { t } = useTranslation();

  // 🛡️ Logic to filter insights based on active partition visibility
  const displayInsights = useMemo(() => {
    // In this engine, "insights" provided via props are already pre-filtered 
    // based on the listener targeting /users/{uid} or /households/{hid}
    return insights.slice(0, 5);
  }, [insights]);

  return (
    <div className="p-6 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm h-full flex flex-col relative overflow-hidden">
      
      {/* 🛡️ Partition Indicator Badge */}
      <div className="absolute top-6 right-6 flex items-center gap-1.5 px-3 py-1 bg-slate-50 dark:bg-slate-900 rounded-full border border-slate-100 dark:border-slate-800">
        {activeScope === 'PERSONAL' || !currentHouseholdId ? (
          <Shield className="w-2.5 h-2.5 text-emerald-500" />
        ) : activeScope === 'SHARED' ? (
          <Users className="w-2.5 h-2.5 text-indigo-500" />
        ) : (
          <Layers className="w-2.5 h-2.5 text-violet-500" />
        )}
        <span className="text-[8px] font-black uppercase tracking-tighter text-slate-500">
          {currentHouseholdId ? activeScope : 'Personal'}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Lightbulb className="w-4 h-4 text-amber-500" />
        <h3 className="text-sm font-black uppercase text-slate-400 dark:text-slate-500 tracking-wider">
          {t('dashboard.spark_engine.title', 'Intelligence Feed')}
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 no-scrollbar">
        {displayInsights && displayInsights.length > 0 ? (
          displayInsights.map((spark, index) => (
            <div 
              key={index} 
              className={clsx(
                "p-4 rounded-2xl border-l-4 transition-all hover:translate-x-1",
                activeScope === 'SHARED' 
                  ? "bg-indigo-50/50 dark:bg-indigo-900/10 border-indigo-500/50" 
                  : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              )}
            >
              <div className="flex justify-between items-start mb-1">
                <p className="text-[10px] font-black uppercase text-indigo-600 dark:text-indigo-400">
                  {spark.category}
                </p>
                {/* 🛡️ Metadata showing if this is a personal or shared insight */}
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
                  {spark.scope || 'Global'}
                </span>
              </div>
              <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed font-medium">
                {spark.content}
              </p>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 text-center py-8">
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-full mb-4">
              <Lightbulb className="w-8 h-8 text-slate-200" />
            </div>
            <p className="text-xs font-bold text-slate-400 italic">
              {t('dashboard.spark_engine.no_content', 'Neural pathways are resting. No sparks found.')}
            </p>
          </div>
        )}
      </div>

      {/* --- ENGINE FOOTER --- */}
      <div className="mt-4 pt-4 border-t border-slate-50 dark:border-slate-800 flex justify-between items-center">
        <p className="text-[8px] font-black text-slate-300 uppercase tracking-[0.2em]">
          Deterministic Sync v2.0
        </p>
        {!currentHouseholdId && (
          <div className="flex items-center gap-1">
            <Shield className="w-2.5 h-2.5 text-emerald-500" />
            <span className="text-[7px] font-black text-emerald-500/60 uppercase">Privacy Verified</span>
          </div>
        )}
      </div>
    </div>
  );
};